import type { EconomyConfig, StepTier, DurationTier } from "../types";
import { useLocale } from "../../../context/LanguageContext";

// ─── Section wrapper ─────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Stat({ label, value, unit }: { label: string; value: number | string | null | undefined; unit?: string }) {
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">
        {typeof value === "number" ? value.toLocaleString() : String(value ?? "—")}
        {unit && <span className="ml-0.5 text-muted-foreground">{unit}</span>}
      </span>
    </div>
  );
}

// ─── Tier tables ─────────────────────────────────────────────────────────────

function StepTiersTable({ tiers }: { tiers: StepTier[] }) {
  const { t } = useLocale();
  return (
    <div className="mt-2">
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("adminUI.economy.stepTiers")}</p>
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t("adminUI.economy.colRange")}</th>
              <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">{t("adminUI.economy.colKoinsPerThousand")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tiers.map((tier, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 text-foreground">
                  {tier.fromSteps.toLocaleString()}–{tier.toSteps?.toLocaleString() ?? "∞"} {t("adminUI.economy.unit.steps")}
                </td>
                <td className="px-3 py-1.5 text-right font-medium text-foreground">{tier.koinsPerThousand}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DurationTiersTable({ tiers }: { tiers: DurationTier[] }) {
  const { t } = useLocale();
  return (
    <div className="mt-2">
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("adminUI.economy.durationTiers")}</p>
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t("adminUI.economy.colRange")}</th>
              <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">{t("adminUI.economy.colRate")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tiers.map((tier, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 text-foreground">
                  {tier.fromMinutes}–{tier.toMinutes ?? "∞"} {t("adminUI.economy.unit.min")}
                </td>
                <td className="px-3 py-1.5 text-right font-medium text-foreground">{tier.rate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main overview ───────────────────────────────────────────────────────────

interface EconomyOverviewProps {
  config: EconomyConfig;
}

export function EconomyOverview({ config }: EconomyOverviewProps) {
  const { t } = useLocale();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t("adminUI.economy.sectionStepsToKoins")}>
        <StepTiersTable tiers={config.stepTiers} />
        <Stat label={t("adminUI.economy.stat.dailyStepKoinsCap")} value={config.dailyStepKoinsCap} unit="Koins" />
      </Section>

      <Section title={t("adminUI.economy.sectionExerciseToKoins")}>
        <Stat label={t("adminUI.economy.stat.scaleFactor")} value={config.exerciseScaleFactor} />
        <DurationTiersTable tiers={config.durationTiers} />
        <Stat label={t("adminUI.economy.stat.dailyExerciseKoinsCap")} value={config.dailyExerciseKoinsCap} unit="Koins" />
      </Section>

      <Section title={t("adminUI.economy.sectionFairness")}>
        <Stat label={t("adminUI.economy.stat.ageBaseline")} value={config.fairnessAgeBaseline} />
        <Stat label={t("adminUI.economy.stat.ageCoefficient")} value={config.fairnessAgeCoefficient} />
        <Stat label={t("adminUI.economy.stat.bmiBaseline")} value={config.fairnessBMIBaseline} />
        <Stat label={t("adminUI.economy.stat.bmiCoefficient")} value={config.fairnessBMICoefficient} />
        <Stat label={t("adminUI.economy.stat.maxMultiplier")} value={config.fairnessMaxMultiplier} />
      </Section>

      <Section title={t("adminUI.economy.sectionStreaks")}>
        <Stat label={t("adminUI.economy.stat.requiredDays")} value={config.streakRequiredDays} />
        <Stat label={t("adminUI.economy.stat.reward")} value={config.streakRewardKoins} unit="Koins" />
        <Stat label={t("adminUI.economy.stat.minStepsQualifyingDay")} value={config.streakMinStepsForQualifyingDay} />
        <Stat label={t("adminUI.economy.stat.minActivityMinutes")} value={config.streakMinActivityMinutesForQualifyingDay} unit={t("adminUI.economy.unit.min")} />
      </Section>

      <Section title={t("adminUI.economy.sectionChallenges")}>
        <Stat label={t("adminUI.economy.stat.minBuyIn")} value={config.challengeMinBuyIn} unit="Koins" />
        <Stat label={t("adminUI.economy.stat.maxBuyIn")} value={config.challengeMaxBuyIn} unit="Koins" />
        <Stat label={t("adminUI.economy.stat.minParticipantsPaid")} value={config.challengeMinParticipantsForPaid} />
        <Stat label={t("adminUI.economy.stat.prizePoolPct")} value={(config.challengePrizePoolPercentage * 100).toFixed(0)} unit="%" />
        <Stat label={t("adminUI.economy.stat.houseBurnPct")} value={(config.challengeHouseBurnPercentage * 100).toFixed(0)} unit="%" />
        <Stat label={t("adminUI.economy.stat.twoPlayerWinnerPct")} value={(config.challengeTwoPlayerWinnerPct * 100).toFixed(0)} unit="%" />
        {config.challengePrizeDistribution.length > 0 && (
          <div className="mt-2">
            <p className="mb-1 text-xs font-medium text-muted-foreground">{t("adminUI.economy.prizeDistribution")}</p>
            <div className="flex flex-wrap gap-2">
              {config.challengePrizeDistribution.map((d) => (
                <span
                  key={d.rank}
                  className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-foreground"
                >
                  #{d.rank}: {(d.percentage * 100).toFixed(0)}%
                </span>
              ))}
            </div>
          </div>
        )}
      </Section>

      <Section title={t("adminUI.economy.sectionMarketplaceAndOther")}>
        <Stat label={t("adminUI.economy.stat.referencePrice")} value={config.referencePricePerTenEuro} unit="Koins" />
        <Stat label={t("adminUI.economy.stat.manualSourcePenalty")} value={config.manualSourcePenalty} />
        <Stat label={t("adminUI.economy.stat.onboardingBonus")} value={config.onboardingBonusAmount} unit="Koins" />
        <Stat label={t("adminUI.economy.stat.activityCooldown")} value={config.activityCooldownMinutes} unit={t("adminUI.economy.unit.min")} />
        <Stat label={t("adminUI.economy.stat.fallbackMET")} value={config.fallbackMETValue} />
      </Section>

      <Section title={t("adminUI.economy.sectionAntiCheat")}>
        <Stat label={t("adminUI.economy.stat.maxManualSteps")} value={config.maxManualStepsPerActivity} />
        <Stat label={t("adminUI.economy.stat.maxManualDistance")} value={config.maxManualDistanceKmPerActivity} unit="km" />
        <Stat label={t("adminUI.economy.stat.maxManualCalories")} value={config.maxManualCaloriesPerActivity} />
        <Stat label={t("adminUI.economy.stat.maxStepsPerMinute")} value={config.maxStepsPerMinute} />
        <Stat label={t("adminUI.economy.stat.anomalyRollingMultiplier")} value={config.anomalyRollingMultiplier} />
      </Section>
    </div>
  );
}
